/** Optional account UI. Guests are still created automatically by WebSocket hello. */
import { useState, type FormEvent, type ReactElement } from "react";
import { logout, submitAuth, type AuthAction } from "../net/auth.js";
import { loadSessionToken, saveSessionToken } from "../prefs.js";
import { navigate } from "../router/hashRoute.js";

function errorText(code: string): string {
  if (code === "invalidCredentials") return "Email or password was not accepted.";
  if (code === "emailTaken") return "That email is already in use.";
  if (code === "invalidSession") return "Your guest session expired; reconnect and try again.";
  return "Could not complete that request. You can keep playing as a guest.";
}

export function AccountScreen(): ReactElement {
  const [mode, setMode] = useState<AuthAction>("upgrade");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    const result = await submitAuth(mode, email.trim(), password, loadSessionToken());
    setPending(false);
    if (!result.ok) {
      setError(errorText(result.error));
      return;
    }
    if (result.sessionToken !== null) saveSessionToken(result.sessionToken);
    // A new app instance sends hello with the saved, authenticated token.
    window.location.hash = "#/";
    window.location.reload();
  }

  async function startNewGuest(): Promise<void> {
    await logout(loadSessionToken());
    saveSessionToken("");
    window.location.hash = "#/";
    window.location.reload();
  }

  return (
    <main
      data-testid="account-screen"
      style={{
        width: "min(420px, calc(100% - 32px))",
        margin: "48px auto",
        padding: 24,
        border: "1px solid #3a4757",
        borderRadius: 8,
      }}
    >
      <h1>Account</h1>
      <p>Play without signing in, or save this guest account so you can return to it later.</p>
      <p>
        {(["upgrade", "login", "signup"] as const).map((next) => (
          <button
            key={next}
            type="button"
            aria-pressed={mode === next}
            onClick={() => setMode(next)}
          >
            {next === "upgrade"
              ? "Save guest account"
              : next === "login"
                ? "Sign in"
                : "Create account"}
          </button>
        ))}
      </p>
      <form onSubmit={(event) => void submit(event)}>
        <label htmlFor="account-email">
          Email
          <input
            id="account-email"
            name="username"
            data-testid="account-email"
            type="email"
            required
            maxLength={320}
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label htmlFor="account-password">
          Password
          <input
            id="account-password"
            name="password"
            data-testid="account-password"
            type="password"
            required
            minLength={8}
            maxLength={256}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        {error ? <p role="alert">{error}</p> : null}
        <p>
          <button data-testid="account-submit" type="submit" disabled={pending}>
            {pending
              ? "Please wait…"
              : mode === "upgrade"
                ? "Save account"
                : mode === "login"
                  ? "Sign in"
                  : "Create account"}
          </button>
          <button type="button" onClick={() => navigate({ kind: "home" })}>
            Continue as guest
          </button>
          <button type="button" onClick={() => void startNewGuest()}>
            Sign out / start a new guest
          </button>
        </p>
      </form>
    </main>
  );
}
